import type VueI18n from 'vue-i18n'
import type {AxiosInstance} from 'axios'
import {Http} from './http'
import {IUrlsConfig, makeUrls} from './urls'
import {CaseTimeService} from './caseTime'
import {PassportService} from './passport'
import {Timer} from "../utils/Timer";
import {IContextConfig} from "../context";
import {
    E_CaseTimeAction,
    E_CaseTimeStatus,
    E_CurrentStatus,
    IPutCaseOperationParams,
    IUserCaseItem
} from "./caseTime.types";
import {forceJson} from "../utils/lang";
import {watchDog} from "../utils/WatchDog";
import {CaseTimeAxiosError} from "../utils/errors";

export interface IServiceConfig {
    urls: IUrlsConfig
    i18n: VueI18n
    transfer: AxiosInstance
}

export class Service {
    _transfer: AxiosInstance
    _http: Http
    public _originContextConfig: IContextConfig = undefined
    caseTime: CaseTimeService
    passport: PassportService

    timer: Timer
    watchDog = watchDog

    get userCaseId() {
        return this.currentUserCaseViewData?.user_case_id
    }

    // 值被组件的外界watch
    get CurrentStatus(): E_CurrentStatus {
        if (!this.currentUserCaseViewData) {
            return E_CurrentStatus.NONE
        }
        const {
            status,
            action
        } = this.currentUserCaseViewData
        if (status === E_CaseTimeStatus.WORKING) {
            return E_CurrentStatus.WORKING
        } else if (status === E_CaseTimeStatus.DONE && action === E_CaseTimeAction.PAUSE) {
            return E_CurrentStatus.PAUSE
        } else {
            return E_CurrentStatus.DONE
        }
    }

    get isEnded() {
        return [E_CurrentStatus.DONE, E_CurrentStatus.NONE].includes(this.CurrentStatus);
    }

    get isPaused() {
        return this.CurrentStatus === E_CurrentStatus.PAUSE;
    }

    get isWorking() {
        return this.CurrentStatus === E_CurrentStatus.WORKING;
    }

    constructor(config: IServiceConfig) {
        const {
            i18n,
        } = config
        this._http = new Http({
            i18n,
            transferGetter: this.getTransfer,
        })
        this._transfer = config.transfer
        const urls = makeUrls(config.urls)
        this.caseTime = new CaseTimeService(
            this._http,
            urls.case_time)
        this.passport = new PassportService(urls.passport)
        this.timer = new Timer()
    }

    getTransfer = () => {
        return this._transfer
    }

    setTransfer(transfer: AxiosInstance) {
        this._transfer = transfer
    }

    private _serverEnabled = false
    private _needAutoPopup: boolean = false

    get enabled() {
        if (this._originContextConfig?.disabled) {
            return false
        }
        return this._serverEnabled
    }

    get needAutoPopup() {
        return this.enabled && this._needAutoPopup
    }

    async updateEnabled() {
        const {data} = await this.caseTime.getEnableCaseView()
        this._serverEnabled = data.authorized
        this._needAutoPopup = data.case_popup
    }


    currentUserCaseViewData?: IUserCaseItem = undefined

    async updateCurrentUserCaseView() {
        await this.caseTime.getCurrentUserCaseView()
            .then(d => {
                this.setCurrentUserCaseViewData(d.data || '')
            })
    }

    private setCurrentUserCaseViewData(d:IUserCaseItem|'') {
        if(!d) {
            this.currentUserCaseViewData = undefined;
        }else {
            this.currentUserCaseViewData = forceJson(d)
        }

        this.updateTimer()
    }


    async putAndUpdateCase(_putOperationParams: IPutCaseOperationParams) {
        const putOperationParams = {..._putOperationParams}
        if (_putOperationParams.action !== E_CaseTimeAction.START) {
            putOperationParams.user_case_id = this.currentUserCaseViewData?.user_case_id
        }
        const {data} = await this.caseTime.putCaseOperation(putOperationParams)
        this.setCurrentUserCaseViewData(data)
    }


    updateTimer() {
        const action = this.currentUserCaseViewData?.action || E_CaseTimeAction.END
        const targetStatus = this.currentUserCaseViewData?.status || E_CaseTimeStatus.DONE;
        const durSec = this.currentUserCaseViewData?.display_case_duration || 0
        switch (action) {
            case E_CaseTimeAction.START:
            case E_CaseTimeAction.RESTART: {
                this.timer.startTiming(durSec * 1000);
                break;
            }
            case E_CaseTimeAction.PAUSE: {
                this.timer.setCurrentDuration(durSec * 1000)
                this.timer.pauseTiming()
                break;
            }
            case E_CaseTimeAction.END: {
                this.timer.clearTiming();
                this.stopWatchDog();
                break;
            }
        }
        if(targetStatus === E_CaseTimeStatus.WORKING) {
            this.startWatchDog();
            this.timer.startTiming(durSec * 1000);
        } else {
            if(action !== E_CaseTimeAction.PAUSE) {
                this.timer.clearTiming();
            }
            this.stopWatchDog();
        }
    }

    // 用户离开了，最好6分钟后问下是不是暂停了,UI不要留下还在运行的假象
    private _userGoneLastCheck = undefined

    private _watchDogUnSubscribe: () => void = () => {
    }

    private startWatchDog() {
        this.stopWatchDog();
        this._watchDogUnSubscribe = this.watchDog.subscribe((isActivate) => {
            clearInterval(this._userGoneLastCheck);
            if(isActivate) {
                this.sendAndUpdateByHeartBeat()
            }else{
                // 用户离开了，最好6分钟后问下是不是暂停了
                // @ts-ignore
                // this._userGoneLastCheck = setInterval(() => this.updateCurrentUserCaseView(), 1000 * 60 * 6.1)
            }

        })
        this.sendAndUpdateByHeartBeat()
    }

    private stopWatchDog() {
        this._watchDogUnSubscribe()
        clearInterval(this._userGoneLastCheck);
    }

    private async sendAndUpdateByHeartBeat() {
        if (!this.isWorking) {
            return
        }
        if (!this.userCaseId) {
            return
        }
        try {
            const {data} = await this.caseTime.heartBeat(this.userCaseId)
            if (typeof data === "number" || typeof data === "string") {
                this.timer.setCurrentDuration(Number(data) * 1000)
            }
        } catch (e) {
            if ((e as CaseTimeAxiosError).numeric_error_code == 610006) {
                await this.updateCurrentUserCaseView()
            }
        }

    }

}
